import type { Therapist } from '../../stores/chatStore'
import IntroVideoCircle from './IntroVideoCircle'
import { useUserLanguage } from '../../hooks/useUserLanguage'

interface EmptyChatProps {
  therapist?: Therapist | null
  onPromptClick?: (prompt: string) => void
}

const greetings: Record<string, { title: string; subtitle: string; prompts: string[] }> = {
  wendy: {
    title: 'Привет! Я Венди',
    subtitle: 'Я здесь, чтобы поддержать вас. Расскажите, что у вас на душе?',
    prompts: [
      'Чувствую тревогу',
      'Хочу разобраться в себе',
      'Сложные отношения',
    ],
  },
  artem: {
    title: 'Привет! Я Артём',
    subtitle: 'Готов честно поговорить о том, что вас беспокоит.',
    prompts: [
      'Застрял на месте',
      'Нужна прямая обратная связь',
      'Хочу изменений',
    ],
  },
  finch: {
    title: 'Добро пожаловать',
    subtitle: 'Я координатор вашей терапии. С чего начнём?',
    prompts: [
      'Первый раз здесь',
      'Хочу познакомиться',
      'Есть вопросы о терапии',
    ],
  },
  harold: {
    title: 'Добро пожаловать',
    subtitle: 'Я координатор вашей терапии. С чего начнём?',
    prompts: [
      'Первый раз здесь',
      'Хочу познакомиться',
      'Есть вопросы о терапии',
    ],
  },
  una: {
    title: 'Привет... Я Уна',
    subtitle: 'Интересно, что привело тебя ко мне?',
    prompts: [
      'Скучно',
      'Одиноко',
      'Поболтать',
    ],
  },
}

export default function EmptyChat({ therapist, onPromptClick }: EmptyChatProps) {
  const type = therapist?.type || 'wendy'
  const content = greetings[type] || greetings.wendy
  const isUna = type === 'una'
  const lang = useUserLanguage()

  const handlePromptClick = (prompt: string) => {
    if (onPromptClick) {
      onPromptClick(prompt)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4 animate-fade-in">
      {/* Intro video circle (autoplay muted, tap to unmute) */}
      <div className="mb-6">
        <IntroVideoCircle
          therapistType={type}
          lang={lang}
          size="lg"
          autoPlay={true}
          fallbackAvatarUrl={therapist?.avatarUrl || `/avatars/${type}.png`}
        />
      </div>

      {/* Greeting - Una gets lighter text for darker background */}
      <h2 className={`text-2xl font-semibold mb-2 ${
        isUna ? 'text-white drop-shadow-md' : 'text-neutral-800'
      }`}>
        {content.title}
      </h2>
      <p className={`max-w-sm mb-8 ${
        isUna ? 'text-white/90 drop-shadow-sm' : 'text-neutral-500'
      }`}>
        {content.subtitle}
      </p>

      {/* Quick prompts - Una gets more contrast */}
      <div className="flex flex-wrap justify-center gap-2 max-w-md">
        {content.prompts.map((prompt) => (
          <button
            key={prompt}
            onClick={() => handlePromptClick(prompt)}
            className={`px-4 py-2.5 rounded-2xl text-sm
                     border shadow-soft-sm
                     hover:shadow-soft
                     transition-all duration-200 active:scale-[0.98] ${
              isUna
                ? 'bg-white/90 text-violet-900 border-violet-200 hover:border-violet-300'
                : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-300'
            }`}
          >
            {prompt}
          </button>
        ))}
      </div>

      {/* Hint - Una gets lighter text */}
      <p className={`mt-8 text-xs ${
        isUna ? 'text-white/70' : 'text-neutral-400'
      }`}>
        Или просто начните писать
      </p>
    </div>
  )
}
